import { jsx, jsxs } from '@emotion/react/jsx-runtime';
import { compound, createComponentWithRef, useSlot, SlotProvider } from '@nettbureau/core';
import { useTransitionState, Transition } from '@nettbureau/motion';
import { useCss } from '@nettbureau/styling';
import { mergeProps } from '@react-aria/utils';
import { DismissButton, Overlay } from 'react-aria';
import { Backdrop } from '../backdrop.js';
import { useOverlayContext, OverlayProvider } from '../overlay-context.js';
import { useOverlay } from '../use-overlay.js';
import { usePopover } from './use-popover.js';

/**
 * Map of placement axis to the opposite axis for direction-based transition types.
 */ const placementTransitionMap = {
    top: 'bottom',
    bottom: 'top',
    left: 'right',
    right: 'left',
    center: 'center'
};
/**
 * A component that provides a popover-style overlay for displaying content relative to a trigger
 * element. The overlay can be either modal or non-modal, allowing you to control whether it hides
 * other content.
 */ const Popover = compound('Popover', createComponentWithRef((props, ref)=>{
    const [mergedProps, mergedRef] = useSlot('Popover', props, ref);
    const { children, defaultOpen, open, transition, ...otherProps } = mergedProps;
    const contextState = useOverlayContext();
    const localState = useOverlay(mergedProps);
    const state = open != null || defaultOpen != null || !contextState ? localState : contextState;
    const [transitionState, transitionProps] = useTransitionState();
    if (!state.isOpen && transitionState === 'hidden') return null;
    return /*#__PURE__*/ jsx(OverlayProvider, {
        value: state,
        children: /*#__PURE__*/ jsx(PopoverOverlay, {
            ...otherProps,
            ref: mergedRef,
            defaultOpen: defaultOpen,
            open: open,
            transition: mergeProps(transitionProps, transition),
            transitionState: transitionState,
            children: children
        })
    });
}), {
    DismissButton
});
/**
 * Internal component for a popover overlay. This is a separate component, because we do not wish to
 * call the `usePopover` hook when the popover is not open. This is to prevent "overflow: hidden"
 * being added to the root element.
 */ const PopoverOverlay = createComponentWithRef((props, forwardedRef)=>{
    const [mergedProps, popoverRef] = useSlot('PopoverOverlay', props, forwardedRef);
    const { children, className, css, disableBackdrop = false, isNonModal, transition, transitionState } = mergedProps;
    const [state, { backdropProps, popoverProps }] = usePopover(mergedProps, popoverRef);
    const overrides = {
        DismissButton: {
            onPress: ()=>state.close()
        }
    };
    const styles = useCss([
        css,
        {
            pointerEvents: 'auto'
        }
    ]);
    return /*#__PURE__*/ jsx(SlotProvider, {
        value: overrides,
        children: /*#__PURE__*/ jsxs(Overlay, {
            isExiting: transitionState === 'exiting',
            children: [
                !disableBackdrop && /*#__PURE__*/ jsx(Backdrop, {
                    ...backdropProps,
                    transparent: true
                }),
                /*#__PURE__*/ jsxs("div", {
                    "data-popover": true,
                    ...popoverProps,
                    ref: popoverRef,
                    className: className,
                    css: styles,
                    children: [
                        !isNonModal && /*#__PURE__*/ jsx(DismissButton, {
                            onDismiss: ()=>state.close()
                        }),
                        /*#__PURE__*/ jsx(Transition, {
                            type: "appear",
                            css: {
                                maxHeight: 'inherit'
                            },
                            from: state.placement ? placementTransitionMap[state.placement] : undefined,
                            ...transition,
                            in: state.isOpen,
                            children: children
                        }),
                        /*#__PURE__*/ jsx(DismissButton, {})
                    ]
                })
            ]
        })
    });
});

export { Popover, PopoverOverlay, placementTransitionMap };
//# sourceMappingURL=popover.js.map
